const EMAIL_REGEX =
  /^(([^<>()[\]\\.,;:\s@']+(\.[^<>()[\]\\.,;:\s@']+)*)|('.+'))@((\[[0-9]{1,3}\].[0-9]{1,3}\].[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/

const MOBILE_REGEX =
  /(?!9999999999|8888888888|7777777777|6666666666)(^[6-9][0-9]{9}$)/

export const CONTACT_CONSENT_ERROR_MESSAGES = {
  consentRequired: "Please provide consent to proceed",
  emailRequired: "Email ID is required",
  emailInvalid: "Please enter a valid Email ID",
  mobileRequired: "Mobile number is required",
  mobileInvalid: "Please enter a valid mobile number",
  proposerEmailRequired: "Proposer email ID is required",
  proposerMobileRequired: "Proposer mobile number is required",
}

export const DEFAULT_CONTACT_CONSENT_KEYS = {
  email: "email",
  contactNo: "contactNo",
  mobileNumber: "mobileNumber",
  privacyPolicy: "privacyPolicy",
  contactNumber: "contactNumber",
  contactCountryCode: "contactCountryCode",
  CPContactUsQuestion: "CPContactUsQuestion",
}

export const DEFAULT_PROPOSER_CONTACT_KEYS = {
  proposerEmail: "GOPAproposerEmail",
  proposerContactNo: "GOPAnumber",
  proposerMobileNumber: "GOPAproposerCode",
}

export const CONTACT_CONSENT_CONFIG = {
  pageIndex: 0,
  pageId: "GAINcalculatePremiumPage",
  personalSectionId: "GAINpersonalDetailsSection",
  keys: {
    email: "email",
    contactNo: "contactNo",
    mobileNumber: "mobileNumber",
    privacyPolicy: "privacyPolicy",
  },
  proposerKeys: {
    proposerEmail: "GOPAproposerEmail",
    proposerContactNo: "GOPAnumber",
    proposerMobileNumber: "GOPAproposerCode",
  },
  proposerTrigger: {
    fieldKey: "GOPAslifetobeAssuredandProposer",
    requiredValue: "N",
    CPContactUsQuestion: "CPContactUsQuestion",
  },
}

export const IGFP_CONTACT_CONSENT_CONFIG = {
  pageIndex: 0,
  pageId: "PageIGFPPlusCalculatePremium",
  personalSectionId: "SectionIGFPPlusPersonelDetails",
  keys: {
    email: "email",
    contactNo: "contactNo",
    mobileNumber: "mobileNumber",
    privacyPolicy: "privacyPolicy",
  },
  proposerKeys: {
    proposerEmail: "GOPAproposerEmail",
    proposerContactNo: "GOPAnumber",
    proposerMobileNumber: "GOPAproposerCode",
  },
  proposerTrigger: {
    fieldKey: "GOPAslifetobeAssuredandProposer",
    requiredValue: "N",
  },
}

export const FIRST_PAGE_PERSONAL_SECTION_IDS = [
  "GAINpersonalDetailsSection",
  "CPPersonalDetailsSection",
  "SectionGuaranteedSavingsPersonelDetails",
  "SectionGOPAPersonelDetails",
  "SectionFlexiEdgeCalculatePremiumPersonalDetails",
  "SectionIGFPPlusPersonelDetails",
]

export function hasFieldValue(value) {
  if (value === null || value === undefined) return false
  if (typeof value === "string") return value.trim() !== ""
  if (typeof value === "boolean") return value
  return Boolean(value)
}

export function normalizeContactNo(value = "") {
  return String(value).replace(/\D/g, "")
}

function getSectionDataFromJourneyData(journeyData = [], config) {
  const page = journeyData[config.pageIndex]
  if (!page?.pageData?.length) return {}

  const section =
    page.pageData.find(item => item.sectionId === config.personalSectionId) ||
    page.pageData.find(item =>
      FIRST_PAGE_PERSONAL_SECTION_IDS.includes(item.sectionId)
    ) ||
    page.pageData[0]

  return section?.sectionData || {}
}

export function isProposerContactRequired(sectionData = {}, config) {
  const trigger = config?.proposerTrigger
  if (!trigger?.fieldKey) return false
  return sectionData[trigger.fieldKey] === trigger.requiredValue
}

export function getContactConsentSnapshot(
  sectionData = {},
  keys = DEFAULT_CONTACT_CONSENT_KEYS,
  proposerKeys = null,
  config = null
) {
  const contactNo =
    sectionData[keys.contactNo] ?? sectionData[keys.contactNumber] ?? ""

  const snapshot = {
    email: sectionData[keys.email] ?? "",
    contactNo,
    mobileNumber:
      sectionData[keys.mobileNumber] ??
      sectionData[keys.contactCountryCode] ??
      "+91",
    privacyPolicy: Boolean(sectionData[keys.privacyPolicy]),
    cpContactUsQuestion: sectionData[keys.CPContactUsQuestion],
    customerConsent: sectionData.customerConsent,
    requiresProposer: false,
    proposerEmail: "",
    proposerContactNo: "",
    proposerMobileNumber: "+91",
  }

  if (proposerKeys) {
    snapshot.requiresProposer = isProposerContactRequired(
      sectionData,
      config || { proposerTrigger: {} }
    )
    snapshot.proposerEmail = sectionData[proposerKeys.proposerEmail] ?? ""
    snapshot.proposerContactNo =
      sectionData[proposerKeys.proposerContactNo] ?? ""
    snapshot.proposerMobileNumber =
      sectionData[proposerKeys.proposerMobileNumber] ?? "+91"
  }

  return snapshot
}

export function isContactEntered(snapshot) {
  return hasFieldValue(snapshot.email) || hasFieldValue(snapshot.contactNo)
}

export function isConsentRequiredOnFirstPage(snapshot) {
  if (snapshot.customerConsent === "Y") return false
  if (snapshot.cpContactUsQuestion === "N") return false
  if (snapshot.cpContactUsQuestion === "Y") return true
  return isContactEntered(snapshot)
}

export function shouldShowBiContactConsentPopup(snapshot) {
  const laMissing =
    !hasFieldValue(snapshot.email) ||
    !hasFieldValue(snapshot.contactNo) ||
    !snapshot.privacyPolicy

  if (laMissing) return true

  if (snapshot.requiresProposer) {
    return (
      !hasFieldValue(snapshot.proposerEmail) ||
      !hasFieldValue(snapshot.proposerContactNo)
    )
  }

  return false
}

export function canSendBiEmailFromJourneyData(
  journeyData = [],
  config = CONTACT_CONSENT_CONFIG
) {
  const snapshot = getContactConsentFromJourneyData(journeyData, config)
  if (!hasFieldValue(snapshot.email)) {
    return false
  }
  if (snapshot.requiresProposer && !hasFieldValue(snapshot.proposerEmail)) {
    return false
  }
  return true
}

export function isFirstPageContactConsentStep(
  currentPageIndex,
  currentSectionIndex,
  showFirstSectionFields
) {
  return (
    currentPageIndex === 0 &&
    currentSectionIndex === 0
  )
}

export function validateFirstPageContactConsent(
  sectionData,
  keys = DEFAULT_CONTACT_CONSENT_KEYS,
  config = IGFP_CONTACT_CONSENT_CONFIG
) {
  const snapshot = getContactConsentSnapshot(sectionData, keys, DEFAULT_PROPOSER_CONTACT_KEYS, config)

  const errors = {}

  if (isConsentRequiredOnFirstPage(snapshot) && !snapshot.privacyPolicy) {
    errors[keys.privacyPolicy] = CONTACT_CONSENT_ERROR_MESSAGES.consentRequired
  }

  if (snapshot.cpContactUsQuestion === "Y" || snapshot.customerConsent === "Y") {
    validateEmailField(snapshot.email, errors, keys.email, CONTACT_CONSENT_ERROR_MESSAGES.emailRequired)
    validateMobileField(snapshot.contactNo, errors, keys.contactNo, CONTACT_CONSENT_ERROR_MESSAGES.mobileRequired)
    
    if (snapshot.requiresProposer) {
      validateEmailField(snapshot.proposerEmail, errors, DEFAULT_PROPOSER_CONTACT_KEYS.proposerEmail, CONTACT_CONSENT_ERROR_MESSAGES.proposerEmailRequired)
      validateMobileField(snapshot.proposerContactNo, errors, DEFAULT_PROPOSER_CONTACT_KEYS.proposerContactNo, CONTACT_CONSENT_ERROR_MESSAGES.proposerMobileRequired)
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
    snapshot,
  }
}

function validateEmailField(email, errors, errorKey, requiredMessage) {
  const normalized = (email || "").trim().toLowerCase()
  if (!hasFieldValue(normalized)) {
    errors[errorKey] = requiredMessage
  } else if (!EMAIL_REGEX.test(normalized)) {
    errors[errorKey] = CONTACT_CONSENT_ERROR_MESSAGES.emailInvalid
  }
  return normalized
}

function validateMobileField(contactNo, errors, errorKey, requiredMessage) {
  const normalized = normalizeContactNo(contactNo)
  if (!hasFieldValue(normalized)) {
    errors[errorKey] = requiredMessage
  } else if (!MOBILE_REGEX.test(normalized)) {
    errors[errorKey] = CONTACT_CONSENT_ERROR_MESSAGES.mobileInvalid
  }
  return normalized
}

export function validateBiContactConsentPopup(values) {
  const errors = {}

  const email = validateEmailField(
    values.email,
    errors,
    "email",
    CONTACT_CONSENT_ERROR_MESSAGES.emailRequired
  )
  const contactNo = validateMobileField(
    values.contactNo,
    errors,
    "contactNo",
    CONTACT_CONSENT_ERROR_MESSAGES.mobileRequired
  )

  if (!values.privacyPolicy) {
    errors.privacyPolicy = CONTACT_CONSENT_ERROR_MESSAGES.consentRequired
  }

  let proposerEmail = ""
  let proposerContactNo = ""
  let proposerMobileNumber = values.proposerMobileNumber || "+91"

  if (values.requiresProposer) {
    proposerEmail = validateEmailField(
      values.proposerEmail,
      errors,
      "proposerEmail",
      CONTACT_CONSENT_ERROR_MESSAGES.proposerEmailRequired
    )
    proposerContactNo = validateMobileField(
      values.proposerContactNo,
      errors,
      "proposerContactNo",
      CONTACT_CONSENT_ERROR_MESSAGES.proposerMobileRequired
    )
    proposerMobileNumber = values.proposerMobileNumber || "+91"
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
    normalizedValues: {
      email,
      contactNo,
      mobileNumber: values.mobileNumber || "+91",
      privacyPolicy: Boolean(values.privacyPolicy),
      requiresProposer: Boolean(values.requiresProposer),
      proposerEmail,
      proposerContactNo,
      proposerMobileNumber,
    },
  }
}

export function getContactConsentFromJourneyData(
  journeyData = [],
  config = CONTACT_CONSENT_CONFIG
) {
  const sectionData = getSectionDataFromJourneyData(journeyData, config)
  return getContactConsentSnapshot(
    sectionData,
    config.keys,
    config.proposerKeys,
    config
  )
}

export function mergeContactConsentIntoJourneyData(
  journeyData = [],
  values,
  config = CONTACT_CONSENT_CONFIG
) {
  const updatedJourneyData = JSON.parse(JSON.stringify(journeyData || []))
  const pageIndex = config.pageIndex ?? 0

  if (!updatedJourneyData[pageIndex]) {
    updatedJourneyData[pageIndex] = {
      pageId: config.pageId,
      pageData: [],
    }
  }

  const pageData = updatedJourneyData[pageIndex].pageData || []
  let sectionIndex = pageData.findIndex(
    item => item.sectionId === config.personalSectionId
  )

  if (sectionIndex === -1) {
    pageData.unshift({
      sectionId: config.personalSectionId,
      sectionData: {},
    })
    sectionIndex = 0
  }

  const keys = config.keys
  const proposerKeys = config.proposerKeys || {}
  const sectionDataPatch = {
    ...(pageData[sectionIndex]?.sectionData || {}),
    [keys.email]: values.email,
    [keys.contactNo]: values.contactNo,
    [keys.mobileNumber]: values.mobileNumber || "+91",
    [keys.privacyPolicy]: values.privacyPolicy,
  }

  if (values.requiresProposer && proposerKeys.proposerEmail) {
    sectionDataPatch[proposerKeys.proposerEmail] = values.proposerEmail
    sectionDataPatch[proposerKeys.proposerContactNo] = values.proposerContactNo
    sectionDataPatch[proposerKeys.proposerMobileNumber] =
      values.proposerMobileNumber || "+91"
  }

  pageData[sectionIndex] = {
    ...pageData[sectionIndex],
    sectionData: sectionDataPatch,
  }

  updatedJourneyData[pageIndex].pageData = pageData
  return updatedJourneyData
}

export function buildContactConsentConditionPatch(
  config = CONTACT_CONSENT_CONFIG,
  values
) {
  const keys = config.keys
  const proposerKeys = config.proposerKeys || {}
  const patch = {
    [`${config.pageId}.${config.personalSectionId}.${keys.email}`]: values.email,
    [`${config.pageId}.${config.personalSectionId}.${keys.contactNo}`]:
      values.contactNo,
    [`${config.pageId}.${config.personalSectionId}.${keys.mobileNumber}`]:
      values.mobileNumber || "+91",
    [`${config.pageId}.${config.personalSectionId}.${keys.privacyPolicy}`]:
      values.privacyPolicy,
  }

  if (values.requiresProposer && proposerKeys.proposerEmail) {
    patch[
      `${config.pageId}.${config.personalSectionId}.${proposerKeys.proposerEmail}`
    ] = values.proposerEmail
    patch[
      `${config.pageId}.${config.personalSectionId}.${proposerKeys.proposerContactNo}`
    ] = values.proposerContactNo
    patch[
      `${config.pageId}.${config.personalSectionId}.${proposerKeys.proposerMobileNumber}`
    ] = values.proposerMobileNumber || "+91"
  }

  return patch
}
